import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, Save, Upload, User, Car, CreditCard, FileText, Smartphone, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DatePickerController } from '@/components/ui/date-picker';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { getPhoneValidationMessage } from '@/lib/phoneValidation';
import { toast } from 'sonner';
import { useCreateDriver } from '@/api-services/hooks/useDriver';
import { useActiveVehicleCategories } from '@/api-services/hooks/useVehicleCategory';
import { DriverDTO } from '@/api-services/hooks/adminTypes';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';

const driverFormSchema = z.object({
  // Personal Information
  phoneNumber: z.string()
    .min(1, 'Phone number is required')
    .superRefine((val, ctx) => {
      const msg = getPhoneValidationMessage(val);
      if (msg) ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg });
    }),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
dateOfBirth: z
  .string()
  .optional()
  .refine((val) => {
    if (!val) return true; // optional case

    const dob = new Date(val);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age >= 18;
  }, {
    message: "Driver must be at least 18 years old",
  }),
  gender: z.string().optional(),
  address: z.string().optional(),
  emergencyContactNumber: z.string().optional(),
  preferredLanguage: z.string().optional(),
  // Accept URL or data URL (from file uploads)
  profilePhotoUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Government ID
  govtIdType: z.string().optional(),
  govtIdNumber: z.string().optional(),
  govtIdFrontUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  govtIdBackUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  drivingLicenseFrontUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  drivingLicenseBackUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  selfieWithIdUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Vehicle Information
  vehicleType: z.string().optional(),
  vehicleMake: z.string().optional(),
  vehicleModel: z.string().optional(),
  vehicleRegistrationNumber: z.string().optional(),
  yearOfManufacture: z.number().min(1900).max(new Date().getFullYear() + 1).optional().or(z.literal('')),
  vehicleColor: z.string().optional(),
  seatingCapacity: z.number().min(1).optional().or(z.literal('')),
  luggageCapacity: z.number().min(0).optional().or(z.literal('')),
  ownershipDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  insuranceDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),
  roadTaxDocUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Banking Information
  bankAccountHolderName: z.string().optional(),
  bankName: z.string().optional(),
  accountNumber: z.string().optional(),
  ifscCode: z.string().optional(),
  swiftCode: z.string().optional(),
  paymentFrequency: z.string().optional(),

  // Device & App Information
  deviceType: z.string().optional(),
  osVersion: z.string().optional(),
  fcmToken: z.string().optional(),
  appVersion: z.string().optional(),
  geolocationPermission: z.boolean().optional(),

  // Additional Fields
  signupMethod: z.string().optional(),
  otpVerified: z.boolean().optional(),
  termsConsent: z.boolean().optional(),
  ewalletId: z.string().optional(),
});

type DriverFormData = z.infer<typeof driverFormSchema>;

const AddDriverPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const { duplicateErrors, checkEmail, checkPhone } = useDuplicateCheck('driver');
  const phoneRef = React.useRef<HTMLDivElement>(null);

  const { data: vehicleCategoriesData } = useActiveVehicleCategories();
  const vehicleCategories: any[] = Array.isArray(vehicleCategoriesData) ? vehicleCategoriesData : [];

  const { mutate: createDriver, isLoading } = useCreateDriver({
    onSuccess: (data) => {
      toast.success('Driver created successfully!');
      navigate('/admin/drivers');
    },
    onError: (error) => {
      toast.error(`Failed to create driver: ${error.message}`);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    setFocus,
    control,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverFormSchema),
    defaultValues: {
      otpVerified: false,
      geolocationPermission: false,
      termsConsent: false,
    },
  });

  // Handle file upload - convert to base64 data URL
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof DriverFormData
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Store the file for potential later upload
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));

      // Convert to data URL (base64) for immediate use
      // In production, you'd upload this file to your server first
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setValue(fieldName as any, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to render file upload field with URL option
  const renderDocumentField = (
    fieldName: keyof DriverFormData,
    label: string,
    placeholder: string,
    optional?: boolean
  ) => {
    const hasFile = uploadedFiles[fieldName as string];
    const currentValue = watch(fieldName);

    return (
      <div className="space-y-2 md:col-span-2">
        <Label>{optional ? `${label} (Optional)` : label}</Label>
        <div className="space-y-3">
          {/* File Upload Option */}
          <div className="flex items-center gap-3">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload(e, fieldName)}
              className="flex-1"
            />
            {hasFile && (
              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                {hasFile.name}
              </span>
            )}
          </div>

          <Input
            {...register(fieldName)}
            placeholder={placeholder}
            value={currentValue || ''}
          />
          {errors[fieldName] && (
            <p className="text-sm text-destructive">{errors[fieldName]?.message}</p>
          )}
          {currentValue && (
            <p className="text-xs text-muted-foreground">
              {currentValue.startsWith('data:') ? 'File uploaded' : 'URL provided'}
            </p>
          )}
        </div>
      </div>
    );
  };

  const onSubmit = (data: DriverFormData) => {
      let phoneCountryCode = '';
        let mobileNumber = '';
    
        if (data.phoneNumber) {
          const phone = parsePhoneNumberFromString(data.phoneNumber);
        
          if (phone) {
            phoneCountryCode = `+${phone.countryCallingCode}`; // +91
            mobileNumber = phone.nationalNumber;              // 9876543210
          }
        }

         let emergencyContactNumber = '';
        let emergencyPhoneCountryCode = '';
    
        if (data.emergencyContactNumber) {
          const phone = parsePhoneNumberFromString(data.emergencyContactNumber);
        
          if (phone) {
            emergencyPhoneCountryCode = `+${phone.countryCallingCode}`; // +91
            emergencyContactNumber = phone.nationalNumber;              // 9876543210
          }
        }
    
    // Clean the data before sending to API
    const driverData: DriverDTO = {
      phoneNumber: mobileNumber,
      phoneCountryCode: phoneCountryCode,
      email: data.email?.trim() || undefined,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      fullName: `${data.firstName.trim()} ${data.lastName.trim()}`,
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender || undefined,
      address: data.address?.trim() || undefined,
      emergencyContactNumber: emergencyContactNumber,
      emergencyPhoneCountryCode: emergencyPhoneCountryCode,
      preferredLanguage: data.preferredLanguage || undefined,
      profilePhotoUrl: data.profilePhotoUrl?.trim() || undefined,
      govtIdType: data.govtIdType || undefined,
      govtIdNumber: data.govtIdNumber || undefined,
      govtIdFrontUrl: data.govtIdFrontUrl || undefined,
      govtIdBackUrl: data.govtIdBackUrl || undefined,
      drivingLicenseFrontUrl: data.drivingLicenseFrontUrl || undefined,
      drivingLicenseBackUrl: data.drivingLicenseBackUrl || undefined,
      selfieWithIdUrl: data.selfieWithIdUrl || undefined,
      vehicleType: data.vehicleType || undefined,
      vehicleMake: data.vehicleMake?.trim() || undefined,
      vehicleModel: data.vehicleModel?.trim() || undefined,
      vehicleRegistrationNumber: data.vehicleRegistrationNumber?.trim() || undefined,
      yearOfManufacture: data.yearOfManufacture ? Number(data.yearOfManufacture) : undefined,
      vehicleColor: data.vehicleColor || undefined,
      seatingCapacity: data.seatingCapacity ? Number(data.seatingCapacity) : undefined,
      luggageCapacity: data.luggageCapacity ? Number(data.luggageCapacity) : undefined,
      ownershipDocUrl: data.ownershipDocUrl || undefined,
      insuranceDocUrl: data.insuranceDocUrl || undefined,
      roadTaxDocUrl: data.roadTaxDocUrl || undefined,
      bankAccountHolderName: data.bankAccountHolderName?.trim() || undefined,
      bankName: data.bankName?.trim() || undefined,
      accountNumber: data.accountNumber?.trim() || undefined,
      ifscCode: data.ifscCode?.trim() || undefined,
      swiftCode: data.swiftCode?.trim() || undefined,
      paymentFrequency: data.paymentFrequency || undefined,
      deviceType: data.deviceType || undefined,
      osVersion: data.osVersion || undefined,
      fcmToken: data.fcmToken?.trim() || undefined,
      appVersion: data.appVersion || undefined,
      geolocationPermission: data.geolocationPermission || false,
      signupMethod: data.signupMethod || undefined,
      otpVerified: data.otpVerified || false,
      termsConsent: data.termsConsent || false,
      ewalletId: data.ewalletId?.trim() || undefined,
      isDeletedValue: false,
      signupStatus: 'PENDING', // Add default signup status
    };

    createDriver(driverData);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/drivers')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Add New Driver</h1>
            <p className="text-muted-foreground mt-1">Register a new driver to the platform</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="vehicle">
              <Car className="w-4 h-4 mr-2" />
              Vehicle
            </TabsTrigger>
            <TabsTrigger value="banking">
              <CreditCard className="w-4 h-4 mr-2" />
              Banking
            </TabsTrigger>
            <TabsTrigger value="additional">
              <Smartphone className="w-4 h-4 mr-2" />
              Additional
            </TabsTrigger>
          </TabsList>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Enter the driver's personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                    <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <PhoneInput
                          ref={phoneRef}
                          id="phoneNumber"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={async () => {
                            const dup = await checkPhone(field.value);
                            if (dup) phoneRef.current?.querySelector('input')?.focus();
                          }}
                          hasError={!!(errors.phoneNumber || duplicateErrors.phone)}
                        />
                      )}
                    />
                    {(errors.phoneNumber || duplicateErrors.phone) && (
                      <p className="text-sm text-destructive">{errors.phoneNumber?.message || duplicateErrors.phone}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email', {
                        onBlur: async () => {
                          const dup = await checkEmail(getValues('email'));
                          if (dup) setFocus('email');
                        },
                      })}
                      placeholder="driver@example.com"
                    />
                    {(errors.email || duplicateErrors.email) && (
                      <p className="text-sm text-destructive">{errors.email?.message || duplicateErrors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      {...register('firstName')}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-sm text-destructive">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      {...register('lastName')}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">{errors.lastName.message}</p>
                    )}
                  </div>

                  <DatePickerController
                    control={control}
                    name="dateOfBirth"
                    label="Date of Birth"
                    placeholder="Select date of birth"
                    maxDate={new Date()}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select onValueChange={(value) => setValue('gender', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">Male</SelectItem>
                        <SelectItem value="FEMALE">Female</SelectItem>
                        {/* <SelectItem value="OTHER">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem> */}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      {...register('address')}
                      placeholder="123 Main St, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emergencyContactNumber">Emergency Contact Number</Label>
                    <Controller
                      control={control}
                      name="emergencyContactNumber"
                      render={({ field }) => (
                        <PhoneInput
                          id="emergencyContactNumber"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredLanguage">Preferred Language</Label>
                    <Select onValueChange={(value) => setValue('preferredLanguage', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Profile Photo */}
                  {renderDocumentField(
                    'profilePhotoUrl',
                    'Profile Photo',
                    'https://example.com/photo.jpg'
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="signupMethod">Signup Method</Label>
                    <Select onValueChange={(value) => setValue('signupMethod', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select signup method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="otpVerified"
                      {...register('otpVerified')}
                      onCheckedChange={(checked) => setValue('otpVerified', checked as boolean)}
                    />
                    <Label htmlFor="otpVerified" className="cursor-pointer">
                      OTP Verified
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Government ID & Documents</CardTitle>
                <CardDescription>Upload driver identification documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="govtIdType">Government ID Type</Label>
                    <Select onValueChange={(value) => setValue('govtIdType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="national_id">National ID</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="govtIdNumber">Government ID Number</Label>
                    <Input
                      id="govtIdNumber"
                      {...register('govtIdNumber')}
                      placeholder="ID number"
                    />
                  </div>

                  {/* Government ID Front */}
                  {renderDocumentField(
                    'govtIdFrontUrl',
                    'Government ID Front',
                    'https://example.com/doc-front.jpg'
                  )}

                  {/* Government ID Back */}
                  {renderDocumentField(
                    'govtIdBackUrl',
                    'Government ID Back',
                    'https://example.com/doc-back.jpg'
                  )}

                  {/* Driving License Front */}
                  {renderDocumentField(
                    'drivingLicenseFrontUrl',
                    'Driving License Front',
                    'https://example.com/license-front.jpg'
                  )}

                  {/* Driving License Back */}
                  {renderDocumentField(
                    'drivingLicenseBackUrl',
                    'Driving License Back',
                    'https://example.com/license-back.jpg'
                  )}

                  {/* Selfie with ID */}
                  {renderDocumentField(
                    'selfieWithIdUrl',
                    'Selfie with ID',
                    'https://example.com/selfie.jpg'
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicle Tab */}
          <TabsContent value="vehicle" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Vehicle Information</CardTitle>
                <CardDescription>Enter vehicle details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType">Vehicle Category</Label>
                    <Select onValueChange={(value) => {
                      const cat = vehicleCategories.find((c: any) => c.vehicleCategoryId.toString() === value);
                      if (cat) {
                        setValue('vehicleType', cat.vehicleCategoryName);
                        setValue('seatingCapacity', cat.maxPassengerCount || '');
                        setValue('luggageCapacity', cat.maxLuggageVolume || '');
                      }
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vehicle category" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleCategories.map((c: any) => (
                          <SelectItem key={c.vehicleCategoryId} value={c.vehicleCategoryId.toString()}>
                            {c.vehicleCategoryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleMake">Vehicle Make</Label>
                    <Input
                      id="vehicleMake"
                      {...register('vehicleMake')}
                      placeholder="Toyota"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleModel">Vehicle Model</Label>
                    <Input
                      id="vehicleModel"
                      {...register('vehicleModel')}
                      placeholder="Camry"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleRegistrationNumber">Registration Number</Label>
                    <Input
                      id="vehicleRegistrationNumber"
                      {...register('vehicleRegistrationNumber')}
                      placeholder="ABC-1234"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="yearOfManufacture">Year of Manufacture</Label>
                    <Input
                      id="yearOfManufacture"
                      type="number"
                      {...register('yearOfManufacture', { valueAsNumber: true })}
                      placeholder="2020"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                    {errors.yearOfManufacture && (
                      <p className="text-sm text-destructive">{errors.yearOfManufacture.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicleColor">Vehicle Color</Label>
                    <Input
                      id="vehicleColor"
                      {...register('vehicleColor')}
                      placeholder="White"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seatingCapacity">Seating Capacity</Label>
                    <Input
                      id="seatingCapacity"
                      type="number"
                      {...register('seatingCapacity', { valueAsNumber: true })}
                      placeholder="Auto-filled from vehicle category"
                      readOnly
                      className="bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="luggageCapacity">Luggage Capacity</Label>
                    <Input
                      id="luggageCapacity"
                      type="number"
                      {...register('luggageCapacity', { valueAsNumber: true })}
                      placeholder="Auto-filled from vehicle category"
                      readOnly
                      className="bg-muted"
                    />
                  </div>

                  {/* Ownership Document */}
                  {renderDocumentField(
                    'ownershipDocUrl',
                    'Ownership Document',
                    'https://example.com/ownership.pdf'
                  )}

                  {/* Insurance Document */}
                  {renderDocumentField(
                    'insuranceDocUrl',
                    'Insurance Document',
                    'https://example.com/insurance.pdf',
                    true
                  )}

                  {/* Road Tax Document */}
                  {renderDocumentField(
                    'roadTaxDocUrl',
                    'Road Tax Document',
                    'https://example.com/roadtax.pdf'
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banking Tab */}
          <TabsContent value="banking" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Banking Information</CardTitle>
                <CardDescription>Enter banking details for payments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bankAccountHolderName">Account Holder Name</Label>
                    <Input
                      id="bankAccountHolderName"
                      {...register('bankAccountHolderName')}
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Input
                      id="bankName"
                      {...register('bankName')}
                      placeholder="ABC Bank"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      {...register('accountNumber')}
                      placeholder="1234567890"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ifscCode">IFSC Code</Label>
                    <Input
                      id="ifscCode"
                      {...register('ifscCode')}
                      placeholder="ABCD0123456"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="swiftCode">SWIFT Code</Label>
                    <Input
                      id="swiftCode"
                      {...register('swiftCode')}
                      placeholder="SWIFTCODE123"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="paymentFrequency">Payment Frequency</Label>
                    <Select onValueChange={(value) => setValue('paymentFrequency', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Information Tab */}
          <TabsContent value="additional" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
                <CardDescription>Device and app information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="deviceType">Device Type</Label>
                    <Select onValueChange={(value) => setValue('deviceType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select device type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="android">Android</SelectItem>
                        <SelectItem value="ios">iOS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="osVersion">OS Version</Label>
                    <Input
                      id="osVersion"
                      {...register('osVersion')}
                      placeholder="14.0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="appVersion">App Version</Label>
                    <Input
                      id="appVersion"
                      {...register('appVersion')}
                      placeholder="1.0.0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fcmToken">FCM Token</Label>
                    <Input
                      id="fcmToken"
                      {...register('fcmToken')}
                      placeholder="FCM token"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ewalletId">E-Wallet ID</Label>
                    <Input
                      id="ewalletId"
                      {...register('ewalletId')}
                      placeholder="wallet_123456"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="geolocationPermission"
                      {...register('geolocationPermission')}
                      onCheckedChange={(checked) => setValue('geolocationPermission', checked as boolean)}
                    />
                    <Label htmlFor="geolocationPermission" className="cursor-pointer">
                      Geolocation Permission Granted
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="termsConsent"
                      {...register('termsConsent')}
                      onCheckedChange={(checked) => setValue('termsConsent', checked as boolean)}
                    />
                    <Label htmlFor="termsConsent" className="cursor-pointer">
                      Terms & Conditions Consented
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/drivers')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-gradient-primary hover:opacity-90"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Driver
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddDriverPage;

